/**
 * SectorPicker — premium multi-sector selector (Network-iA universal).
 *
 * UI :
 *  - Grid of sector cards with emoji + label + description
 *  - Toggle multi-select (couleur active = gradient signature)
 *  - "Mon métier perso" card → reveals text input below
 *  - Save button (mutation) + autosave optional later
 *
 * Used in : OnboardingPage (early step), /profil (edit), maybe paramètres
 * Inea later.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle2, Plus, Sparkles } from 'lucide-react';
import {
  useSectorCatalog,
  useUserSectors,
  useUpdateUserSectors,
} from '@/hooks/useSectors';

interface Props {
  /** If provided, called after a successful save (e.g. onboarding step done). */
  onSaved?: () => void;
  /** Compact mode for embedding in /profil. */
  compact?: boolean;
}

export function SectorPicker({ onSaved, compact = false }: Props) {
  const { t } = useTranslation();
  const { data: catalog, isLoading: catalogLoading } = useSectorCatalog();
  const { data: userSectors, isLoading: userLoading } = useUserSectors();
  const update = useUpdateUserSectors();

  const [selected, setSelected] = useState<string[]>([]);
  const [customLabel, setCustomLabel] = useState('');
  const [didSave, setDidSave] = useState(false);

  useEffect(() => {
    if (userSectors) {
      setSelected(userSectors.sectors);
      setCustomLabel(userSectors.custom_sector_label ?? '');
    }
  }, [userSectors]);

  const toggle = useCallback((key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);

  const save = useCallback(() => {
    update.mutate(
      {
        sectors: selected,
        custom_sector_label: selected.includes('custom') ? customLabel.trim() : null,
      },
      {
        onSuccess: () => {
          setDidSave(true);
          setTimeout(() => setDidSave(false), 2500);
          onSaved?.();
        },
      },
    );
  }, [selected, customLabel, update, onSaved]);

  if (catalogLoading || userLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="animate-spin text-ink/60" />
      </div>
    );
  }

  if (!catalog) {
    return (
      <p className="text-center text-sm text-red-600">
        {t('sectors.loadError')}
      </p>
    );
  }

  const hasCustom = selected.includes('custom');

  return (
    <div className="space-y-4">
      {!compact && (
        <header className="space-y-1.5">
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t('sectors.kicker')}
          </p>
          <h2 className="font-display text-2xl font-800 tracking-[-0.02em] text-ink">
            {t('sectors.title')}
          </h2>
          <p className="text-[13.5px] leading-[1.55] text-ink/60">
            {t('sectors.intro')}
          </p>
        </header>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {catalog.map((s) => {
          const active = selected.includes(s.key);
          return (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className={`group flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
                active
                  ? 'border-violet-400 bg-gradient-to-br from-violet-50 to-pink-50 shadow-[0_8px_24px_-12px_rgba(168,85,247,0.4)]'
                  : 'border-ink/10 bg-white hover:border-ink/25 hover:bg-ink/[0.02]'
              }`}
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${
                  active ? 'bg-gradient-to-br from-violet-500 to-pink-500' : 'bg-ink/5'
                }`}
              >
                {s.emoji}
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-display text-[14px] font-800 leading-tight text-ink">
                    {t(`sectors.list.${s.key}.label`, { defaultValue: s.label })}
                  </span>
                  {active && (
                    <CheckCircle2 size={14} className="shrink-0 text-violet-600" />
                  )}
                </span>
                <p className="mt-1 text-[12px] leading-[1.4] text-ink/55">
                  {t(`sectors.list.${s.key}.description`, { defaultValue: s.description })}
                </p>
              </span>
            </button>
          );
        })}

        {/* "Mon métier perso" — open custom */}
        <button
          onClick={() => toggle('custom')}
          className={`group flex items-start gap-3 rounded-2xl border-2 border-dashed p-4 text-left transition ${
            hasCustom
              ? 'border-pink-400 bg-pink-50/50'
              : 'border-ink/15 bg-white hover:border-ink/30'
          }`}
        >
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
              hasCustom ? 'bg-pink-500 text-white' : 'bg-ink/5 text-ink/40'
            }`}
          >
            {hasCustom ? <Sparkles size={18} /> : <Plus size={18} />}
          </span>
          <span className="flex-1">
            <span className="flex items-center gap-2">
              <span className="font-display text-[14px] font-800 text-ink">
                {t('sectors.custom')}
              </span>
              {hasCustom && (
                <CheckCircle2 size={14} className="shrink-0 text-pink-600" />
              )}
            </span>
            <p className="mt-1 text-[12px] leading-[1.4] text-ink/55">
              {t('sectors.customHint')}
            </p>
          </span>
        </button>
      </div>

      {hasCustom && (
        <label className="block">
          <span className="text-[11px] font-700 uppercase tracking-wide text-ink/55">
            {t('sectors.customLabel')}
          </span>
          <input
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder={t('sectors.customPlaceholder')}
            maxLength={80}
            className="mt-1.5 block w-full rounded-lg border border-ink/10 bg-white px-3 py-2.5 text-sm focus:border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-300/30"
          />
        </label>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-ink/5 pt-4">
        <p className="text-[12px] text-ink/55">
          {selected.length === 0
            ? t('sectors.noneSelected')
            : t('sectors.selected', { count: selected.length })}
        </p>
        <button
          onClick={save}
          disabled={update.isPending || (hasCustom && !customLabel.trim())}
          className="hero-cta-light-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {update.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" /> {t('sectors.saving')}
            </>
          ) : didSave ? (
            <>
              <CheckCircle2 size={14} /> Enregistré
            </>
          ) : (
            <>{t('sectors.save')}</>
          )}
        </button>
      </div>
    </div>
  );
}
